// src/components/course/SidebarControls.js
import React from 'react';
import ActionControls from './ActionControls';
import ToggleSwitch from '../common/ToggleSwitch';
import { useTranslation } from '../../hooks/useTranslation';
import EnhancedSelect from '../common/EnhancedSelect';
import { levelMapping } from '../../utils/mappings'; 

const SidebarControls = React.forwardRef((props, ref) => {
  const {
    level,
    setLevel,
    filter,
    setFilter,
    grammarFilter,
    setGrammarFilter,
    kanjiComplexityMode,
    setKanjiComplexityMode,
    kanjiComplexityValue,
    setKanjiComplexityValue,
    isSelectMode,
    isLoggedIn,
    contentType,
    isMobileView = false,
    ...actionControlsProps
  } = props;
  const [showTranslation, setTranslationVisibility] = useTranslation();

  return (
    <aside>
      <div className="space-y-6">
        <div ref={ref}>
          <h3 className="text-lg font-semibold text-content-primary">Filters</h3>
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="level-filter" className="block text-sm font-medium text-content-secondary pb-1">
                Level
              </label>
              <EnhancedSelect
                id="level-filter"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                disabled={isSelectMode}
                options={[
                  { value: 'all', label: 'All' },
                  ...Object.entries(levelMapping)
                    .filter(([key]) => contentType === 'words' || key !== '0')
                    .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
                    .map(([key, label]) => ({ value: key, label })),
                ]}
              />
            </div>

            {isLoggedIn && (
              <div>
                <label htmlFor="status-filter" className="block text-sm font-medium text-content-secondary pb-1">
                  Status
                </label>
                <EnhancedSelect
                  id="status-filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  disabled={isSelectMode}
                  options={[
                    { value: 'all', label: `All ${contentType}` },
                    { value: 'not-learned', label: 'Not Learned' },
                  ]}
                />
              </div>
            )}

            {contentType === 'words' && (
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-content-secondary mr-2">Show only grammar words</label>
                <ToggleSwitch enabled={grammarFilter} setEnabled={setGrammarFilter} disabled={isSelectMode} />
              </div>
            )}

            {contentType === 'sentences' && (
              <div>
                <label className="block text-sm font-medium text-content-secondary pb-1">
                  Kanji Level
                </label>
                <EnhancedSelect
                  value={kanjiComplexityMode}
                  onChange={(e) => {
                    setKanjiComplexityMode(e.target.value);
                    setKanjiComplexityValue('');
                  }}
                  disabled={isSelectMode}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'kana', label: 'Kana only' },
                    { value: 'range', label: 'WK Level (1–60)' },
                    { value: 'nonwk', label: 'Non-WK (漢61+)' },
                  ]}
                />
                {kanjiComplexityMode === 'range' && (
                  <div className="relative mt-2">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-lg pointer-events-none" />
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={kanjiComplexityValue}
                      onChange={(e) => setKanjiComplexityValue(e.target.value)}
                      onKeyDown={(e) => {
                        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                        if (allowed.includes(e.key)) return;
                        if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                        if (!/^\d$/.test(e.key)) e.preventDefault();
                      }}
                      disabled={isSelectMode}
                      placeholder="Enter level 1–60"
                      className="relative z-10 w-full px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg backdrop-blur-sm border-2 border-border text-content-primary font-medium text-sm sm:text-base hover:border-primary/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed outline-none focus:border-primary [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{ background: 'var(--color-background-secondary)', MozAppearance: 'textfield' }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-lg font-semibold text-content-primary">Display</h3>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-content-secondary mr-2">Show Translation</label>
              <ToggleSwitch enabled={showTranslation} setEnabled={setTranslationVisibility} disabled={isSelectMode} />
            </div>
          </div>
        </div>

        {isLoggedIn && contentType === 'words' && (
          <div className="my-6 py-6 border-y border-border">
            <h3 className="text-lg font-semibold text-content-primary">Actions</h3>
            <div className="mt-4">
              <ActionControls {...actionControlsProps} isSelectMode={isSelectMode} isMobileView={isMobileView} />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
});

SidebarControls.displayName = 'SidebarControls';

export default SidebarControls;
